/**
 * 阴影 Design Token 系统
 * 定义完整的阴影效果规范
 */

import type { ShadowTokens } from './types';

// 阴影 Token 定义
export const shadowTokens: ShadowTokens = {
  // 基础阴影
  none: 'none',
  
  // 细微阴影 - 用于轻微的层次感
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  
  // 小阴影 - 用于按钮、小卡片
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  
  // 标准阴影 - 用于卡片、面板
  base: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  
  // 中等阴影 - 用于悬浮卡片、下拉菜单
  md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  
  // 大阴影 - 用于对话框、抽屉
  lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  
  // 超大阴影 - 用于模态框、重要容器
  xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  
  // 特大阴影 - 用于全屏覆盖、特殊效果
  '2xl': '0 50px 100px -20px rgba(0, 0, 0, 0.25)',
  
  // 内阴影 - 用于输入框、凹陷效果
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
};

// 彩色阴影 (基于主色调)
export const coloredShadows = {
  // 主色阴影 - 用于主要按钮、重要元素
  primary: '0 4px 14px 0 rgba(37, 99, 235, 0.15)',
  primaryHover: '0 6px 20px 0 rgba(37, 99, 235, 0.25)',
  
  // 成功色阴影
  success: '0 4px 14px 0 rgba(16, 185, 129, 0.15)',
  successHover: '0 6px 20px 0 rgba(16, 185, 129, 0.25)',
  
  // 警告色阴影
  warning: '0 4px 14px 0 rgba(245, 158, 11, 0.15)',
  warningHover: '0 6px 20px 0 rgba(245, 158, 11, 0.25)',
  
  // 错误色阴影
  error: '0 4px 14px 0 rgba(239, 68, 68, 0.15)',
  errorHover: '0 6px 20px 0 rgba(239, 68, 68, 0.25)',
  
  // 信息色阴影
  info: '0 4px 14px 0 rgba(37, 99, 235, 0.15)',
  infoHover: '0 6px 20px 0 rgba(37, 99, 235, 0.25)',
};

// 阴影使用规范
export const shadowUsage = {
  none: '平面设计、无层次需求、紧贴背景的元素',
  xs: '分割线、边框替代、极轻微的层次感',
  sm: '按钮默认状态、小标签、徽章、工具提示',
  base: '卡片、面板、表单容器、导航栏',
  md: '悬浮卡片、下拉菜单、弹出框、侧边栏',
  lg: '对话框、抽屉、大型面板、重要容器',
  xl: '模态框、全屏对话框、重要弹窗',
  '2xl': '全屏覆盖、特殊视觉效果、品牌展示',
  inner: '输入框、文本域、凹陷按钮、内嵌容器',
};

// 交互状态阴影
export const interactionShadows = {
  // 悬停状态阴影
  hover: {
    button: '0 6px 20px 0 rgba(0, 0, 0, 0.15)',
    card: '0 12px 28px 0 rgba(0, 0, 0, 0.12)',
    icon: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
  },
  
  // 激活状态阴影
  active: {
    button: '0 2px 8px 0 rgba(0, 0, 0, 0.15)',
    card: '0 4px 12px 0 rgba(0, 0, 0, 0.12)',
    icon: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
  },
  
  // 聚焦状态阴影
  focus: {
    input: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    button: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    card: '0 0 0 2px rgba(37, 99, 235, 0.2)',
  },
  
  // 禁用状态阴影
  disabled: {
    button: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    card: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
  },
};

// 响应式阴影系统
export const responsiveShadows = {
  // 移动端阴影调整 (减弱阴影效果)
  mobile: {
    none: shadowTokens.none,
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    base: '0 2px 4px -1px rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.04)',
    md: '0 6px 12px -2px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
    lg: '0 12px 20px -4px rgba(0, 0, 0, 0.08), 0 6px 8px -4px rgba(0, 0, 0, 0.03)',
    xl: '0 20px 40px -8px rgba(0, 0, 0, 0.2)',
    '2xl': '0 40px 80px -16px rgba(0, 0, 0, 0.2)',
    inner: shadowTokens.inner,
  },
  
  // 平板端阴影调整
  tablet: {
    none: shadowTokens.none,
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.09), 0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 3px 5px -1px rgba(0, 0, 0, 0.09), 0 1px 3px -1px rgba(0, 0, 0, 0.05)',
    md: '0 8px 13px -2px rgba(0, 0, 0, 0.09), 0 3px 5px -1px rgba(0, 0, 0, 0.04)',
    lg: '0 16px 22px -4px rgba(0, 0, 0, 0.09), 0 8px 9px -4px rgba(0, 0, 0, 0.03)',
    xl: '0 22px 45px -10px rgba(0, 0, 0, 0.22)',
    '2xl': '0 45px 90px -18px rgba(0, 0, 0, 0.22)',
    inner: shadowTokens.inner,
  },
  
  // 桌面端阴影 (默认)
  desktop: shadowTokens,
};

// 组件特定阴影配置
export const componentShadows = {
  // 按钮阴影
  button: {
    default: shadowTokens.sm,
    hover: interactionShadows.hover.button,
    active: interactionShadows.active.button,
    focus: interactionShadows.focus.button,
    disabled: interactionShadows.disabled.button,
  },
  
  // 卡片阴影
  card: {
    default: shadowTokens.base,
    hover: interactionShadows.hover.card,
    active: interactionShadows.active.card,
    focus: interactionShadows.focus.card,
    disabled: interactionShadows.disabled.card,
  },
  
  // 下拉菜单阴影
  dropdown: {
    default: shadowTokens.md,
  },
  
  // 对话框阴影
  modal: {
    default: shadowTokens.xl,
  },
  
  // 抽屉阴影
  drawer: {
    default: shadowTokens.lg,
  },
  
  // 工具提示阴影
  tooltip: {
    default: shadowTokens.sm,
  },
  
  // 弹出框阴影
  popover: {
    default: shadowTokens.md,
  },
  
  // 输入框阴影
  input: {
    default: shadowTokens.inner,
    focus: interactionShadows.focus.input,
  },
};

// 导出阴影常量，用于 SCSS 变量兼容
export const shadowConstants = {
  // 基础阴影
  SHADOW_NONE: shadowTokens.none,
  SHADOW_XS: shadowTokens.xs,
  SHADOW_SM: shadowTokens.sm,
  SHADOW_BASE: shadowTokens.base,
  SHADOW_MD: shadowTokens.md,
  SHADOW_LG: shadowTokens.lg,
  SHADOW_XL: shadowTokens.xl,
  SHADOW_2XL: shadowTokens['2xl'],
  SHADOW_INNER: shadowTokens.inner,
  
  // 彩色阴影
  SHADOW_PRIMARY: coloredShadows.primary,
  SHADOW_PRIMARY_HOVER: coloredShadows.primaryHover,
  SHADOW_SUCCESS: coloredShadows.success,
  SHADOW_WARNING: coloredShadows.warning,
  SHADOW_ERROR: coloredShadows.error,
  SHADOW_INFO: coloredShadows.info,
  
  // 交互阴影
  SHADOW_BUTTON_HOVER: interactionShadows.hover.button,
  SHADOW_CARD_HOVER: interactionShadows.hover.card,
  SHADOW_FOCUS: interactionShadows.focus.input,
} as const;